import React, { useEffect, useState } from 'react';
import { X, Phone, Mail, MessageSquare, Users, MapPin, FileText, Send, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import ActivityTimeline from './ActivityTimeline';

const ACTIVITY_TYPES = [
  { key: 'call',       icon: Phone,         label: 'Call',          color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'meeting',    icon: Users,         label: 'Meeting',       color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'email',      icon: Mail,          label: 'Email',         color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'whatsapp',   icon: MessageSquare, label: 'WhatsApp',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'site_visit', icon: MapPin,        label: 'Site Visit',    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'note',       icon: FileText,      label: 'Note',          color: 'bg-slate-50 text-slate-700 border-slate-200' },
];

/**
 * Drawer for displaying & logging activities against a Company or Contact.
 *
 * Props:
 *   entityType: 'company' | 'contact'
 *   entityId: id of the record
 *   entityLabel: human label for the title bar
 *   subtitle: optional secondary line under title
 *   onClose: () => void
 *   metaRows: optional array of {label, value} rendered in the info panel
 */
export default function EntityActivityDrawer({ entityType, entityId, entityLabel, subtitle, onClose, metaRows = [], bottomSlot = null }) {
  const [activeType, setActiveType] = useState('call');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState('');
  const [busy, setBusy] = useState(false);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiBase = entityType === 'company' ? '/crm/companies' : '/crm/contacts';

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`${apiBase}/${entityId}/activity`);
      setComms(r.communications || []);
    } catch (err) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [entityType, entityId]);

  const log = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await apiPost(`${apiBase}/${entityId}/communications`, {
        type: activeType,
        subject: subject.trim(),
        notes: notes.trim() || null,
        duration_minutes: duration ? parseInt(duration) : null,
      });
      setSubject(''); setNotes(''); setDuration('');
      toast.success('Activity logged');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={`${entityType}-detail-drawer`}>
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-[700px] bg-slate-50 border-l border-slate-200 flex flex-col">
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">{entityType}</div>
            <h2 className="font-display text-xl font-bold mt-1 truncate" title={entityLabel}>{entityLabel}</h2>
            {subtitle && <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} data-testid="close-drawer"><X size={18} /></button>
        </div>

        {/* Meta */}
        {metaRows.length > 0 && (
          <div className="bg-white border-b border-slate-200 px-6 py-3 grid grid-cols-2 gap-x-6 gap-y-1.5">
            {metaRows.map((m, idx) => (
              <div key={idx} className="text-xs">
                <span className="text-slate-500 uppercase tracking-wider text-[10px] font-display font-bold">{m.label}</span>
                <div className="text-slate-800 mt-0.5 truncate">{m.value || '—'}</div>
              </div>
            ))}
          </div>
        )}

        {/* Quick-log */}
        <div className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Log Activity</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {ACTIVITY_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveType(t.key)}
                  className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-display font-bold px-2.5 py-1.5 border rounded-sm ${activeType === t.key ? t.color : 'bg-white border-slate-200 text-slate-500 hover:border-[#1F7A6E]'}`}
                  data-testid={`activity-type-${t.key}`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Subject (e.g. Discussed BOQ revision)"
            className="w-full mb-2 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E]"
            data-testid="activity-subject"
          />
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notes / outcome (optional)"
            rows={2}
            className="w-full mb-2 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E]"
            data-testid="activity-notes"
          />
          <div className="flex gap-2 items-end">
            {(activeType === 'call' || activeType === 'meeting') && (
              <div className="flex-shrink-0">
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  placeholder="Duration (min)"
                  className="w-32 px-3 py-2 border border-slate-200 rounded-sm text-sm"
                  data-testid="activity-duration"
                />
              </div>
            )}
            <button
              onClick={log}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
              data-testid="log-activity-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Log {ACTIVITY_TYPES.find(t => t.key === activeType)?.label}
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Activity Timeline ({comms.length})</div>
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : comms.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8">No activity logged yet. Log your first interaction above.</div>
          ) : (
            <ActivityTimeline communications={comms} />
          )}
          {bottomSlot && <div className="mt-6">{bottomSlot}</div>}
        </div>
      </div>
    </div>
  );
}
